/**
 * Directory-integration OAuth callback path — single source of truth.
 *
 * This is the redirect URI path we hand to a directory provider's
 * authorization server, and which the customer registers verbatim in
 * their IdP application (e.g. the Entra app registration's "Redirect
 * URI"). It is consumed in two places that MUST stay in lockstep:
 *
 *   1. `components/identity/IntegrationsTab.jsx` builds
 *      `${window.location.origin}${DIRECTORY_OAUTH_CALLBACK_PATH}` as the
 *      `redirect_uri` it sends to the provider's authorize endpoint.
 *   2. `main.jsx` mounts `<OAuthCallback />` (ahead of the router) when
 *      the browser lands back on this exact path.
 *
 * Customer setup docs instruct admins to register
 * `…/integrations/oauth-callback`. The `/integrations` prefix is
 * load-bearing: dropping it makes the dashboard send a redirect_uri that
 * doesn't match the customer's registered one, which the IdP rejects
 * (Entra surfaces this as `AADSTS50011` mid-sign-in). Keeping the value
 * here — imported by both the sender and the listener — means the two
 * cannot drift apart again.
 */
export const DIRECTORY_OAUTH_CALLBACK_PATH = "/integrations/oauth-callback";

/**
 * Absolute redirect URI for directory-integration OAuth, rooted at the
 * dashboard origin the user is currently on so it matches the host the
 * customer registered with their IdP.
 *
 * @param {string} origin - the dashboard origin, typically
 *   `window.location.origin` (e.g. `https://staging.originhq.com`).
 * @returns {string} the full redirect URI to send as `redirect_uri`.
 */
export function directoryOauthRedirectUri(origin) {
    return `${origin}${DIRECTORY_OAUTH_CALLBACK_PATH}`;
}
